import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import * as api from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Building2 } from 'lucide-react';

const Cabang = () => {
  const { cabang, pemasukan, pengeluaran, inventori, fetchCabang } = useApp();
  const { isAdmin } = useAuth();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    nama: '',
    alamat: '',
    keterangan: ''
  });

  const resetForm = () => {
    setFormData({
      nama: '',
      alamat: '',
      keterangan: ''
    });
    setEditingItem(null);
  };

  const handleOpenDialog = (item = null) => {
    if (!isAdmin) {
      toast.error('Hanya administrator yang bisa mengubah data cabang');
      return;
    }
    if (item) {
      setEditingItem(item);
      setFormData({
        nama: item.nama,
        alamat: item.alamat || '',
        keterangan: item.keterangan || ''
      });
    } else {
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!isAdmin) {
      toast.error('Akses ditolak untuk mengubah data cabang');
      return;
    }
    try {
      if (!formData.nama.trim()) {
        toast.error('Nama cabang wajib diisi');
        return;
      }

      if (editingItem) {
        await api.updateCabang(editingItem.id, formData);
        toast.success('Cabang berhasil diperbarui');
      } else {
        await api.createCabang(formData);
        toast.success('Cabang berhasil ditambahkan');
      }

      setIsDialogOpen(false);
      resetForm();
      fetchCabang();
    } catch (error) {
      toast.error('Gagal menyimpan data');
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    if (!isAdmin) {
      toast.error('Akses ditolak untuk menghapus cabang');
      return;
    }
    // Check if cabang has any data
    const hasData = 
      pemasukan.some(p => p.cabang_id === id) ||
      pengeluaran.some(p => p.cabang_id === id) ||
      inventori.some(i => i.cabang_id === id);

    if (hasData) {
      if (!window.confirm('Cabang ini memiliki data terkait (pemasukan/pengeluaran/inventori). Data terkait tidak akan dihapus. Lanjutkan hapus cabang?')) {
        return;
      }
    } else {
      if (!window.confirm('Yakin ingin menghapus cabang ini?')) {
        return;
      }
    }

    try {
      await api.deleteCabang(id);
      toast.success('Cabang berhasil dihapus');
      fetchCabang();
    } catch (error) {
      toast.error('Gagal menghapus cabang');
    }
  };

  // Get stats per cabang
  const getCabangStats = (cabangId) => {
    const cabangPemasukan = pemasukan.filter(p => p.cabang_id === cabangId);
    const cabangPengeluaran = pengeluaran.filter(p => p.cabang_id === cabangId);
    const cabangInventori = inventori.filter(i => i.cabang_id === cabangId);

    return {
      totalPemasukan: cabangPemasukan.reduce((sum, p) => sum + p.jumlah, 0),
      totalPengeluaran: cabangPengeluaran.reduce((sum, p) => sum + p.harga_total, 0),
      totalInventori: cabangInventori.reduce((sum, i) => sum + i.jumlah, 0),
      nilaiInventori: cabangInventori.reduce((sum, i) => sum + i.harga_total, 0)
    };
  };

  const formatRupiah = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="space-y-6" data-testid="cabang-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight">Cabang</h1>
          <p className="text-muted-foreground text-sm mt-1">Kelola daftar cabang usaha</p>
        </div>
        {isAdmin && (
          <Button onClick={() => handleOpenDialog()} data-testid="add-cabang-btn">
            <Plus className="h-4 w-4 mr-2" />
            Tambah Cabang
          </Button>
        )}
      </div>

      {/* Summary */}
      <Card className="bg-violet-50 border-violet-200">
        <CardContent className="p-4">
          <p className="text-xs font-semibold uppercase text-violet-700">Total Cabang</p>
          <p className="text-2xl font-black text-violet-600">{cabang.length}</p>
        </CardContent>
      </Card>

      {/* Cabang Cards Grid */}
      {cabang.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {cabang.map((item) => {
            const stats = getCabangStats(item.id);
            return (
              <Card key={item.id} className="bg-white border border-border/60 hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-violet-100 rounded-sm">
                        <Building2 className="h-5 w-5 text-violet-600" />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg">{item.nama}</h3>
                        {item.alamat && (
                          <p className="text-sm text-muted-foreground">{item.alamat}</p>
                        )}
                      </div>
                    </div>
                    {isAdmin && (
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenDialog(item)}
                          data-testid={`edit-cabang-${item.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(item.id)}
                          className="text-rose-500 hover:text-rose-600"
                          data-testid={`delete-cabang-${item.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>

                  {item.keterangan && (
                    <p className="text-sm text-muted-foreground mb-4">{item.keterangan}</p>
                  )}

                  <div className="space-y-2 pt-4 border-t border-border/50">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Pemasukan</span>
                      <span className="font-mono font-bold text-emerald-600">
                        {formatRupiah(stats.totalPemasukan)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Pengeluaran</span>
                      <span className="font-mono font-bold text-rose-600">
                        {formatRupiah(stats.totalPengeluaran)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Inventori</span>
                      <span className="font-mono font-bold text-blue-600">
                        {stats.totalInventori} item ({formatRupiah(stats.nilaiInventori)})
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="bg-white border border-border/60">
          <CardContent className="p-12 text-center">
            <Building2 className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">Belum ada cabang</p>
            {isAdmin && (
              <Button onClick={() => handleOpenDialog()} className="mt-4" data-testid="add-first-cabang-btn">
                <Plus className="h-4 w-4 mr-2" />
                Tambah Cabang Pertama
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Table view for reference */}
      {cabang.length > 0 && (
        <Card className="bg-white border border-border/60">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-zinc-50">
                    <TableHead className="w-[50px]">No</TableHead>
                    <TableHead>Nama Cabang</TableHead>
                    <TableHead>Alamat</TableHead>
                    <TableHead>Keterangan</TableHead>
                    <TableHead className="w-[100px] text-center">Aksi</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cabang.map((item, index) => (
                    <TableRow key={item.id} className="hover:bg-zinc-50/50">
                      <TableCell className="font-mono text-muted-foreground">{index + 1}</TableCell>
                      <TableCell className="font-bold">{item.nama}</TableCell>
                      <TableCell className="text-muted-foreground">{item.alamat || '-'}</TableCell>
                      <TableCell className="text-muted-foreground max-w-[200px] truncate">
                        {item.keterangan || '-'}
                      </TableCell>
                      <TableCell>
                        {isAdmin ? (
                          <div className="flex justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOpenDialog(item)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(item.id)}
                              className="text-rose-500 hover:text-rose-600"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <div className="text-center text-xs text-muted-foreground">Read only</div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dialog Form */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {editingItem ? 'Edit Cabang' : 'Tambah Cabang'}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Form untuk {editingItem ? 'mengedit' : 'menambah'} data cabang
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nama Cabang <span className="text-rose-500">*</span></Label>
              <Input
                value={formData.nama}
                onChange={(e) => setFormData({ ...formData, nama: e.target.value })}
                placeholder="Nama cabang"
                data-testid="input-nama-cabang"
              />
            </div>
            <div className="space-y-2">
              <Label>Alamat</Label>
              <Input
                value={formData.alamat}
                onChange={(e) => setFormData({ ...formData, alamat: e.target.value })}
                placeholder="Alamat cabang (opsional)"
                data-testid="input-alamat-cabang"
              />
            </div>
            <div className="space-y-2">
              <Label>Keterangan</Label>
              <Input
                value={formData.keterangan}
                onChange={(e) => setFormData({ ...formData, keterangan: e.target.value })}
                placeholder="Keterangan (opsional)"
                data-testid="input-keterangan-cabang"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setIsDialogOpen(false)}>
              Batal
            </Button>
            <Button onClick={handleSubmit} data-testid="submit-cabang-btn">
              {editingItem ? 'Simpan' : 'Tambah'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Cabang;
